// shared/review-bands.js — RWB-2: the band walk that turns an email's HTML into ordered
// review blocks, usable by BOTH the server projection and the FE (plain ESM, no DOM).
//
// Ported VERBATIM in semantics from ED-02's frontend/src/lib/source-map.js
// (deriveSections / walkBands / buildSectionMap — Rooster's carry b18db2508, RW-01a) so
// the server's overlay blocks are the SAME bands the FE derives on its own: bands = the
// direct-child <tr> of the single `em_main_table` content shell, peeling single LAYOUT
// wrappers, skipping every tag inside an HTML comment (Outlook MSO ghost tables never
// shift the band count), labels from a preceding `<!-- == NAME == -->` banner. Authored
// anchors win: a band whose <tr> start tag carries data-region (D7 canonical) or data-sec
// keeps that id and is `authored`; the rest are `derived` (display overlay only, C6).
//
// Output = CHAR ranges (the projection's source_range) — the FE lib converts to lines.
// RWB-4 flight 7 F5: quoted > belongs to the attribute, never a band boundary.

const EM_MAIN_OPEN = /<table\b(?:[^>"']|"[^"]*"|'[^']*')*\bclass="[^"]*\bem_main_table\b[^"]*"(?:[^>"']|"[^"]*"|'[^']*')*>/i;
const SEC_TOK = /<!--|-->|<(\/?)(table|tr)\b/gi;
// TE-121 (🌑 sprint review 09-14): ONLY the `<!-- == NAME == -->` banner form labels a band (at least one `=` on each
// side) — the producer's `<!-- SABRE:BEGIN <id> -->` marker before every anchored element, or any other comment, never does
const SEC_COMMENT = /<!--\s*=+\s*([^=<>]+?)\s*=+\s*-->\s*$/;
const WRAPPER_SHELL = /<table\b(?:[^>"']|"[^"]*"|'[^']*')*\bclass="[^"]*\b(?:em_wrapper|em_main|em_full_wrap|em_body)\b[^"]*"(?:[^>"']|"[^"]*"|'[^']*')*>/i;
// D7: data-region is the CANONICAL anchor; data-sec is the legacy fallback. A row carrying
// both binds to data-region whatever the attribute order (Rooster RW-05 finding, 2026-09-04).
const REGION_RE = /\bdata-region="([^"]+)"/i;
const SEC_RE = /\bdata-sec="([^"]+)"/i;
function matchAnchor(startTag) {
  const r = REGION_RE.exec(startTag);
  if (r) return { attr: 'data-region', id: r[1] };
  const c = SEC_RE.exec(startTag);
  return c ? { attr: 'data-sec', id: c[1] } : null;
}
const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
// the human label of an anchor id: the producer's `region:` namespace dropped, separators to spaces, title case
const labelOf = (id) => titleCase(id.replace(/^region:/, '').replace(/[_.:-]+/g, ' ').trim() || id);
// TE-121 C: a comment ends ONLY at its own close — `<!--[if !mso]><!-->` is a complete comment (the revealed
// branch after it is real content), `<!-->` / `<!--->` are the spec's empty comments; an unclosed one runs to the end
function commentEnd(text, i) {
  if (text.startsWith('<!-->', i)) return i + 5;
  if (text.startsWith('<!--->', i)) return i + 6;
  const e = text.indexOf('-->', i + 4);
  return e < 0 ? -1 : e + 3;
}
// TE-121 B: attribute NAMES, parsed in order with each value consumed whole — a lookalike inside a value never counts
function hasAttrName(tagText, name) {
  const nameEnd = tagText.search(/[\s/>]/);
  const attrs = new RegExp(ATTR_TOK.source, 'g'); attrs.lastIndex = nameEnd < 0 ? tagText.length : nameEnd;
  let m;
  while ((m = attrs.exec(tagText))) if (m[1].toLowerCase() === name) return true;
  return false;
}
function tagEnd(text, start) {
  let quote = null;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (quote) { if (c === quote) quote = null; }
    else if (c === '"' || c === "'") quote = c;
    else if (c === '>') return i + 1;
  }
  return -1;
}

// ── RWB-1 authored-first discovery ───────────────────────────────────────────────────
// The executor puts ONE canonical `data-region="<id>"` on the element that wholly owns a
// region — a standalone section <table>, a <tr>, or the grouped CTA region's <td> — so the
// consumer must find anchors on ANY start tag, not only direct <tr> children of the shell.
// Law: start tags only (comments skipped — the Outlook `<!--[if mso]><xml>…` block is a
// comment; quoted attribute values respected — `data-region=` inside a style string, text
// or another attribute's value is never an anchor); the span = the element's full extent
// by an element-context walk; a MALFORMED authored artifact FAILS by name (duplicate id,
// nested/overlapping spans, unclosed element, bad id) instead of degrading to derived ids.
// RWB-1 AMEND-1 + J3 (🌑 J1 ruling: admit EXACTLY the compiler's id grammar): this is the producer's
// `_REGION_ID_RE` VERBATIM (swarm/lib/sabre/adaptive_agent.py + execute.py) — alnum-first, then any of
// [A-Za-z0-9._:-], no length bound. A cross-language tooth (review-bands-anchors.test.mjs) reads the
// pattern out of those Python files and asserts equality, so neither side drifts silently. ONE grammar,
// used by the deriver, the scope tokenizer and the FE; never narrowed by inference (🐦‍⬛ J3).
export const ANCHOR_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;
const TAG_TOK = /<!--|-->|<(\/?)([A-Za-z][\w:-]*)/g;
// attributes are parsed IN ORDER, each value consumed whole, so `data-region=` inside another
// attribute's value (a style url, a title) can never read as the anchor attribute
const ATTR_TOK = /\s+([^\s=>/"']+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>"']+)))?/g;
function regionAttr(tagText) {
  const nameEnd = tagText.search(/[\s/>]/);                 // after `<tag`
  const attrs = new RegExp(ATTR_TOK.source, 'g'); attrs.lastIndex = nameEnd < 0 ? tagText.length : nameEnd;
  let m;
  while ((m = attrs.exec(tagText))) {
    if (m[1].toLowerCase() === 'data-region') return m[2] != null ? m[2] : m[3] != null ? m[3] : (m[4] != null ? m[4] : '');
  }
  return null;
}
export class AnchorError extends Error {
  constructor(code, msg, detail = {}) { super(msg); this.code = code; this.status = 409; this.detail = detail; }
}
// Every start tag / end tag / comment boundary in order: [{kind:'open'|'close', tag, start, end, text}]
function tokens(text, checkpoint) {
  const out = []; const tok = new RegExp(TAG_TOK.source, 'g');
  let m;
  while ((m = tok.exec(text))) {
    checkpoint?.();
    if (m[0] === '<!--') { const close = commentEnd(text, m.index); if (close < 0) break; tok.lastIndex = close; continue; }
    if (m[0] === '-->') continue;                              // a stray close outside a comment is text
    const end = tagEnd(text, m.index);
    if (end < 0) break;
    tok.lastIndex = end;
    out.push({ kind: m[1] === '/' ? 'close' : 'open', tag: m[2].toLowerCase(), start: m.index, end, selfClosing: m[1] !== '/' && text[end - 2] === '/' });
  }
  return out;
}
const VOID_ELEMENTS = new Set('area base br col embed hr img input link meta param source track wbr'.split(' '));
const OPTIONAL_END_ELEMENTS = new Set('p li dt dd option optgroup tr td th tbody thead tfoot colgroup caption rt rp html head body'.split(' '));
function anchorEnd(toks, index, ambient, checkpoint) {
  const root = toks[index];
  if (root.selfClosing) return root.end;
  const stack = [...ambient];
  // Retain enclosing elements so their close can never be mistaken for the
  // anchor's close when a nested authored row/cell itself was left open.
  // discoverAnchors advances this lexical prefix once, using exactly the old
  // prefix close law. It is distinct from the stricter forward close law below.
  const rootAt = stack.length;
  stack.push(root.tag);
  for (let j = index + 1; j < toks.length; j++) {
    if ((j & 1023) === 0) checkpoint?.();
    const t = toks[j];
    if (t.kind === 'open') {
      if (!t.selfClosing && !VOID_ELEMENTS.has(t.tag)) stack.push(t.tag);
      continue;
    }
    const at = stack.lastIndexOf(t.tag);
    if (at < 0) continue;
    // The authored element still needs its OWN explicit end tag. Only descendants
    // may end implicitly: e.g. an inner </table> closes its omitted </td></tr>,
    // not the outer anchored <tr>. A same-tag counter mistakes that inner row for
    // an unclosed outer anchor. Full malformed-HTML checks remain the scope gate's job.
    if (at < rootAt) return -1;
    if (at === rootAt) return t.end;
    if (stack.slice(at + 1).every((tag) => OPTIONAL_END_ELEMENTS.has(tag))) stack.length = at;
  }
  return -1;
}
export function discoverAnchors(text, { checkpoint } = {}) {
  const toks = tokens(text, checkpoint);
  const anchors = [], ambient = [];
  for (let i = 0; i < toks.length; i++) {
    if ((i & 1023) === 0) checkpoint?.();
    const t = toks[i];
    if (t.kind !== 'open') { const at = ambient.lastIndexOf(t.tag); if (at >= 0) ambient.length = at; continue; }
    const id = regionAttr(text.slice(t.start, t.end));
    if (id != null) {
      if (!ANCHOR_ID_RE.test(id)) throw new AnchorError('anchor_invalid_id', `data-region ${JSON.stringify(id)} is not a valid region id`, { id, offset: t.start });
      const end = anchorEnd(toks, i, ambient, checkpoint);
      if (end < 0) throw new AnchorError('anchor_unclosed', `data-region="${id}" on <${t.tag}> never closes`, { id, tag: t.tag, offset: t.start });
      anchors.push({ id, attr: 'data-region', tag: t.tag, start: t.start, end, order: anchors.length });
    }
    if (!t.selfClosing && !VOID_ELEMENTS.has(t.tag)) ambient.push(t.tag);
  }
  // 🌑 cell (b): mixed attribute families are drift — data-sec is legal ONLY when no data-region
  // exists anywhere; an authored artifact that still carries data-sec somewhere is malformed
  if (anchors.length) {
    const legacy = toks.find((t) => t.kind === 'open' && hasAttrName(text.slice(t.start, t.end), 'data-sec'));
    if (legacy) throw new AnchorError('anchor_mixed_families', 'data-sec and data-region anchors cannot coexist (data-region is canonical)', { offset: legacy.start, tag: legacy.tag });
  }
  const seen = new Map();
  for (const a of anchors) {
    if (seen.has(a.id)) throw new AnchorError('anchor_duplicate', `data-region="${a.id}" appears twice`, { id: a.id, offsets: [seen.get(a.id), a.start] });
    seen.set(a.id, a.start);
  }
  for (let k = 1; k < anchors.length; k++) {
    if (anchors[k].start < anchors[k - 1].end) {
      throw new AnchorError('anchor_nested', `data-region="${anchors[k].id}" lies inside data-region="${anchors[k - 1].id}"`, { inner: anchors[k].id, outer: anchors[k - 1].id });
    }
  }
  return anchors;
}

// Walk a shell's direct-child <tr> bands from `from` to its depth-0 </table>.
// Returns ordered [{start, end}] char spans of each band's <tr>…</tr>.
export function walkBands(text, from, { checkpoint } = {}) {
  const tok = new RegExp(SEC_TOK.source, 'gi');
  tok.lastIndex = from;
  const rows = [];
  let depth = 0, curStart = null, m;
  while ((m = tok.exec(text))) {
    checkpoint?.();
    if (m[0] === '<!--') { const close = commentEnd(text, m.index); if (close < 0) break; tok.lastIndex = close; continue; }
    if (m[0] === '-->') continue;
    const end = tagEnd(text, m.index);
    if (end < 0) break;
    tok.lastIndex = end;
    const closing = m[1] === '/', tag = m[2].toLowerCase();
    if (tag === 'table') { if (closing) { if (depth === 0) break; depth--; } else depth++; }
    else if (depth === 0) {
      if (!closing) curStart = m.index;
      else if (curStart != null) { rows.push({ start: curStart, end }); curStart = null; }
    }
  }
  return rows;
}

// Derive the bands of the house shell → [{sec, label, start, end, authored, anchor_attr}].
// Unknown shell (no em_main_table) → [] (graceful: the caller labels it "no bands").
export function deriveBands(text, { checkpoint } = {}) {
  // RWB-1: any data-region anchor → the artifact is AUTHORED: bands are exactly the anchored
  // elements in document order; unanchored content is not a band (non-shipped / metadata /
  // injected regions carry no anchor by the emission law). data-sec is a legacy attribute
  // and is ignored on an authored artifact (D7: data-region is canonical).
  const anchors = discoverAnchors(text, { checkpoint });
  if (anchors.length) {
    return anchors.map((a, i) => {
      checkpoint?.();
      const cm = SEC_COMMENT.exec(text.slice(0, a.start));
      return {
        sec: a.id, label: cm ? titleCase(cm[1].trim()) : labelOf(a.id),
        start: a.start, end: a.end, authored: true, anchor_attr: 'data-region', order: i,
      };
    });
  }
  // legacy (no authored contract anchor): the house-shell row walk, byte-identical to before
  const tm = EM_MAIN_OPEN.exec(text);
  if (!tm) return [];
  let rows = [], from = tm.index + tm[0].length;
  for (let peel = 0; peel < 6; peel++) {
    rows = walkBands(text, from, { checkpoint });
    if (rows.length !== 1) break;
    const inner = WRAPPER_SHELL.exec(text.slice(rows[0].start, rows[0].end));
    if (!inner) break;
    from = rows[0].start + inner.index + inner[0].length;
  }
  return rows.map((r, i) => {
    checkpoint?.();
    const startTag = text.slice(r.start, tagEnd(text, r.start));
    const a = matchAnchor(startTag);
    const cm = SEC_COMMENT.exec(text.slice(0, r.start));
    return {
      sec: a ? a.id : 's' + String(i + 1).padStart(2, '0'),
      label: cm ? titleCase(cm[1].trim()) : (a ? labelOf(a.id) : 'Section ' + (i + 1)),
      start: r.start, end: r.end,
      authored: !!a, anchor_attr: a ? a.attr : null,
      order: i,
    };
  });
}

// Every band's identity in one word: 'authored' when EVERY band carries an anchor,
// 'derived' when none does, 'mixed' otherwise (surfaced, never hidden).
export function bandsIdentity(bands) {
  if (!bands.length) return 'none';
  const n = bands.filter((b) => b.authored).length;
  return n === bands.length ? 'authored' : n === 0 ? 'derived' : 'mixed';
}
